import { type Key } from 'react';
import { FaShip } from 'react-icons/fa';
import { FaPlaneUp, FaTrain, FaTruck } from 'react-icons/fa6';
import {
  Select,
  SelectItem,
  type SelectProps,
} from '@breezeai-frontend/cargo-ui';
import { snakeCase } from 'lodash-es';

import { type BffGetConfigurationsResponseModeOfTransportOption } from '../../../generated/api-client';
import { type RouteDetailsFormData } from './types';

const motNameToIconMap = (
  name: BffGetConfigurationsResponseModeOfTransportOption['name'],
) => {
  switch (name) {
    case 'sea':
      return <FaShip />;
    case 'air':
      return <FaPlaneUp />;
    case 'rail':
      return <FaTrain />;
    case 'road':
      return <FaTruck />;
  }
};

export type SelectedItem =
  | RouteDetailsFormData['primaryMot']
  | RouteDetailsFormData['placeOfLoadingMot']
  | RouteDetailsFormData['placeOfDeliveryMot'];

interface ModeOfTransportSelectProps
  extends Omit<
    SelectProps<BffGetConfigurationsResponseModeOfTransportOption>,
    'children' | 'onSelectionChange'
  > {
  onSelectionChange: (
    item: BffGetConfigurationsResponseModeOfTransportOption,
  ) => void;
  items: BffGetConfigurationsResponseModeOfTransportOption[];
  selectedItem?: SelectedItem;
}

export function ModeOfTransportSelect({
  onSelectionChange,
  items,
  selectedItem,
  ...props
}: ModeOfTransportSelectProps) {
  const handleSelectionChange = (key: Key) => {
    const item = items.find((i) => i.display_name === key);

    if (item) {
      onSelectionChange?.(item);
    }
  };

  return (
    <Select
      aria-label="Select transport"
      placeholder={props.placeholder || 'Select transport'}
      className="w-[140px] min-w-0 capitalize"
      onSelectionChange={handleSelectionChange}
      absoluteErrorMessage={true}
      selectedKey={selectedItem?.display_name ?? null}
      {...props}
    >
      {items.map(({ name, display_name }) => (
        <SelectItem
          data-testid={`select-option-${snakeCase(display_name)}`}
          key={display_name}
          id={display_name}
          textValue={display_name}
          className="gap-2"
        >
          <span className="text-icons-primary">{motNameToIconMap(name)}</span>
          <span className="capitalize">{display_name}</span>
        </SelectItem>
      ))}
    </Select>
  );
}
